import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Shield, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

// 2024 Bağkur basamak tutarları (yaklaşık)
const basamakTutarlari = {
  1: 2500, 2: 2750, 3: 3000, 4: 3250, 5: 3500, 6: 3750, 7: 4000, 8: 4250,
  9: 4500, 10: 4750, 11: 5000, 12: 5500, 13: 6000, 14: 6500, 15: 7000,
  16: 7500, 17: 8000, 18: 9000, 19: 10000, 20: 11000, 21: 12000, 22: 13000,
  23: 14000, 24: 15000
};

const Bagkur = () => {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paidFilter, setPaidFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    period: "",
    basamak: 1,
    amount: "",
    due_date: "",
    paid: false,
    paid_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bagkurRes, employeesRes] = await Promise.all([
        axios.get(`${API}/bagkur`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setRecords(bagkurRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        basamak: parseInt(formData.basamak),
        amount: parseFloat(formData.amount),
        employee_id: formData.employee_id || null
      };
      
      if (editingRecord) {
        await axios.put(`${API}/bagkur/${editingRecord.id}`, data, { withCredentials: true });
        toast.success("Bağkur güncellendi");
      } else {
        await axios.post(`${API}/bagkur`, data, { withCredentials: true });
        toast.success("Bağkur eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu Bağkur kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/bagkur/${id}`, { withCredentials: true });
      toast.success("Bağkur silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const togglePaid = async (record) => {
    try {
      const data = {
        ...record,
        paid: !record.paid,
        paid_date: !record.paid ? new Date().toISOString().split('T')[0] : null
      };
      await axios.put(`${API}/bagkur/${record.id}`, data, { withCredentials: true });
      toast.success(data.paid ? "Bağkur ödendi" : "Bağkur beklemede");
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      employee_id: record.employee_id || "",
      period: record.period,
      basamak: record.basamak,
      amount: record.amount.toString(),
      due_date: record.due_date,
      paid: record.paid,
      paid_date: record.paid_date || "",
      notes: record.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRecord(null);
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setFormData({
      employee_id: "",
      period: nextMonth.toISOString().slice(0, 7),
      basamak: 1,
      amount: basamakTutarlari[1].toString(),
      due_date: `${nextMonth.toISOString().slice(0, 7)}-26`,
      paid: false,
      paid_date: "",
      notes: ""
    });
  };

  const handleBasamakChange = (value) => {
    const basamak = parseInt(value);
    setFormData({
      ...formData,
      basamak,
      amount: (basamakTutarlari[basamak] || 2500).toString()
    });
  };

  const filteredRecords = records.filter(r => {
    const matchesSearch = r.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.period?.includes(search);
    const matchesPaid = paidFilter === "all" || 
      (paidFilter === "paid" && r.paid) || 
      (paidFilter === "unpaid" && !r.paid);
    return matchesSearch && matchesPaid;
  });

  const totalUnpaid = records.filter(r => !r.paid).reduce((sum, r) => sum + r.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bagkur-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Bağkur Takibi</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Ödenmemiş Toplam: <span className="text-rose-400">{formatCurrency(totalUnpaid)}</span>
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-bagkur-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Bağkur
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="bagkur-search"
          />
        </div>
        <Select value={paidFilter} onValueChange={setPaidFilter}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="bagkur-paid-filter">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="paid">Ödenen</SelectItem>
            <SelectItem value="unpaid">Ödenmemiş</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Kişi</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Dönem</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Basamak</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Son Ödeme</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tutar</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record, index) => (
                <motion.tr 
                  key={record.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(record)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`bagkur-row-${record.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-400/10 rounded-sm flex items-center justify-center">
                        <Shield className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm text-zinc-50">{record.employee_name || "Şirket Sahibi"}</p>
                        {record.notes && (
                          <p className="text-xs text-zinc-500">{record.notes}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {record.period}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="px-2 py-1 bg-amber-400/10 text-amber-400 rounded-sm text-sm">
                      {record.basamak}. Basamak
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {record.due_date}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-zinc-50">{formatCurrency(record.amount)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePaid(record); }}
                      className={`mx-auto flex items-center gap-1 px-2 py-1 rounded-sm text-xs transition-colors ${
                        record.paid 
                          ? 'bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20' 
                          : 'bg-rose-400/10 text-rose-400 hover:bg-rose-400/20'
                      }`}
                      data-testid={`toggle-bagkur-paid-${record.id}`}
                    >
                      {record.paid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {record.paid ? 'Ödendi' : 'Bekliyor'}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(record.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-bagkur-${record.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredRecords.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                    {search || paidFilter !== "all" ? "Sonuç bulunamadı" : "Henüz Bağkur kaydı eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingRecord ? "Bağkur Düzenle" : "Yeni Bağkur"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Kişi (Opsiyonel)</Label>
                <Select value={formData.employee_id || "none"} onValueChange={(v) => setFormData({ ...formData, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="bagkur-employee-select">
                    <SelectValue placeholder="Şirket Sahibi" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none">Şirket Sahibi</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Dönem *</Label>
                <Input 
                  type="month"
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="bagkur-period-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Basamak *</Label>
                <Select value={formData.basamak.toString()} onValueChange={handleBasamakChange}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="bagkur-basamak-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                    {Array.from({ length: 24 }, (_, i) => i + 1).map(b => (
                      <SelectItem key={b} value={b.toString()}>{b}. Basamak</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tutar (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="bagkur-amount-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Son Ödeme Tarihi *</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="bagkur-duedate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ödeme Tarihi</Label>
                <Input 
                  type="date"
                  value={formData.paid_date}
                  onChange={(e) => setFormData({ ...formData, paid_date: e.target.value, paid: !!e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="bagkur-paiddate-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="bagkur-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-bagkur-btn"
              >
                {editingRecord ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bagkur;
